import { Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import ToolBar from "../common/ToolBar";
import Content from "./Content";
import Pagination from "components/Pagination";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  actionModeSet,
  StudentsFilterTypes,
  studentsLoaded,
  currentPageStudentList,
  currentPageSet,
  filterSet,
  IStudent,
  searchTextSet,
  selectStudents,
  selectStudentsActionMode,
  selectStudentsPagination,
  selectFilteredStudents,
  selectFilterType,
  totalPagesSet,
  classInfoSet,
} from "features/Students";
import { useFetchStudentsQuery } from "app/services/students";
import { MasterDataContext } from "contexts/MasterDataContext";
import { useContext, useEffect } from "react";
import { ActionType, IFilterOption } from "features/common/Interfaces";
import { useNavigate } from "react-router-dom";
import { classesLoaded, IClass, selectClassById } from "features/Classes";
import { useFetchClassesQuery } from "app/services/classes";
import AddStudentsDetails from "./AddStudents";
import TableSkeleton from "../common/Skeletons/Table";
import useToast from "hooks/useToast";
import { FireBaseContext } from "contexts/FireBase";

const customStyles = {
  root: {
    margin: "20px 0",
    borderRadius: "2px",
    boxShadow: "0 2px 4px 0 rgba(179, 187, 199, 1)",
    background: "rgba(255, 255, 255, 1)",
    padding: "0 10px",
  },
};

interface IStudentsProps {
  classId: string;
}

function Students({ classId }: IStudentsProps) {
  const { logEvent } = useContext(FireBaseContext);
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const {
    state: { schoolDetails },
  } = useContext(MasterDataContext);
  const selectClass = useAppSelector((state) =>
    selectClassById(state, Number.parseInt(classId))
  );
  const pagination = useAppSelector(selectStudentsPagination);
  const filteredStudents = useAppSelector(selectFilteredStudents);
  const dispatch = useAppDispatch();
  const allStudentsList = useAppSelector(selectStudents);
  const filteredStudentsList = useAppSelector(selectFilteredStudents);
  const currentStudentList = useAppSelector(currentPageStudentList);
  const actionMode = useAppSelector(selectStudentsActionMode);
  const getPage = (value: number) => dispatch(currentPageSet(value));

  const { data: classesData } = useFetchClassesQuery(schoolDetails?.schoolId, {
    skip: !schoolDetails?.schoolId,
  });
  const { data: studentsData, isLoading } = useFetchStudentsQuery(classId, {
    skip: !classId,
  });

  useEffect(() => {
    logEvent("VisitSchoolOnboardingStudentSection");
    return () => {
      dispatch(searchTextSet(""));
    };
  }, []);

  useEffect(() => {
    const totalPages = filteredStudents?.length
      ? Math.ceil(filteredStudents.length / pagination.pageSize)
      : 0;
    dispatch(totalPagesSet(totalPages));
  }, [filteredStudents]);

  useEffect(() => {
    if (classesData?._statusCode === 200) {
      const list = classesData._entity?.map((item) => {
        const classInfo: IClass = {
          grade: item._grade,
          teacher: item._teacherName,
          id: item._id,
          name: item._name,
          teacherId: item._teacherId,
          gradeId: item._gradeId,
          students: item._noOfStudents,
        };
        return {
          ...classInfo,
        };
      });
      dispatch(classesLoaded(list));
    }
  }, [JSON.stringify(classesData)]);

  useEffect(() => {
    if (studentsData?._statusCode === 200) {
      const list = studentsData._entity?.map((item) => {
        const studentInfo: IStudent = {
          id: item._id,
          studentId: item._studentSchoolUid ?? "",
          email: item._emailAddress ?? "",
          name: item._name ?? "",
          className: item._className ?? "",
          classId: item._classId,
          grade: item._gradeName ?? "",
          gradeId: item._gradeId,
          code: item._studentUUIDCode ?? "",
          password: item._deepLinkHome ?? "",
          selected: false,
        };
        return {
          ...studentInfo,
        };
      });
      dispatch(studentsLoaded(list ?? []));
    }
  }, [JSON.stringify(studentsData)]);

  useEffect(() => {
    dispatch(classInfoSet(selectClass));
  }, [JSON.stringify(selectClass)]);

  const addAction = () => {
    dispatch(actionModeSet(ActionType.add));
    logEvent("ClickOnAddStudent");
  };

  const getFilterType = (type: { currentTarget: IFilterOption }) => {
    dispatch(
      filterSet(
        StudentsFilterTypes[
          type.currentTarget.value as keyof typeof StudentsFilterTypes
        ]
      )
    );
  };
  const getSearchValue = (value: string) => {
    if (pagination.currentPage !== 1) dispatch(currentPageSet(1));
    dispatch(searchTextSet(value));
  };
  const currentFilter = useAppSelector(selectFilterType);
  const selectedFilter: IFilterOption = {
    value: currentFilter,
    label: t(currentFilter),
  };

  const filterTypes = Object.keys(StudentsFilterTypes).map((type) => ({
    value: type,
    label: type === "Id" ? t("StudentId") : t(type),
  })) as IFilterOption[];
  const deleteAction = () => {
    const selectedStudents = allStudentsList.filter((stu) => stu.selected);
    if (!selectedStudents?.length) {
      toast({
        label: t("Note"),
        message: t("Select students to delete"),
        rtl: i18n.dir() === "rtl",
        type: "info",
        toastId: "student-not-selected",
      });
      return;
    }
    dispatch(actionModeSet(ActionType.delete));
    logEvent("ClickOnDeleteAllStudents");
  };

  return (
    <>
      <AddStudentsDetails
        open={actionMode === ActionType.add}
        onClose={() => dispatch(actionModeSet(ActionType.list))}
      />
      <Grid container direction="column" sx={customStyles.root}>
        <Grid
          item
          xs={12}
          sx={{
            padding: "15px",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            fontSize: "16px",
          }}
        >
          <Typography component="span"
            onClick={() => navigate("/accounts/classes")}
            style={{ color: "rgb(98 195 238)", cursor: "pointer" }}
          >
            {t("All classes")}
          </Typography>
          <span style={{ margin: "0 10px" }}>&gt;</span>
          <span>{selectClass?.name}</span>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderTop: "1px solid rgb(217 217 217)",
          }}
        >
          <ToolBar
            addLabel={t("Add Student")}
            addAction={addAction}
            deleteLabel={t("Delete Students")}
            deleteAction={deleteAction}
            noData={isLoading || !allStudentsList?.length}
            filterConfig={{
              getFilterType,
              getSearchValue,
              selectedFilter,
              filterTypes,
            }}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{
            borderTop: "1px solid rgb(217 217 217)",
          }}
        >
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <Content
              pageList={currentStudentList}
              allList={allStudentsList}
              filteredList={filteredStudentsList}
            />
          )}
        </Grid>
        <Grid item xs={12} sx={{ padding: "20px 0 40px" }}>
          {currentStudentList?.length ? (
            <Pagination
              total={pagination.totalPages}
              defaultPage={pagination.currentPage}
              getPage={getPage}
              spreadStyle
            />
          ) : null}
        </Grid>
      </Grid>
    </>
  );
}

export default Students;
